import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Alert, CircularProgress, Container } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useGetPasswordResetInfoQuery, useResetPasswordMutation } from "../../api/authApi";
import BaseFormPage from "../../components/common/BaseForm/BaseFormPage";
import { GenericTextField } from "../../components/common/FormFields";
import { useFormSubmission } from "../../hooks/useFormSubmission";

const validationSchema = Yup.object({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters long")
    .max(100, "Password cannot exceed 100 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
});

interface FormValues {
  password: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const {
    data: resetInfoResponse,
    isLoading: isLoadingInfo,
    error: infoError,
  } = useGetPasswordResetInfoQuery(token || "", {
    skip: !token,
  });

  const resetInfo = resetInfoResponse?.data;
  const [resetPassword] = useResetPasswordMutation();

  const {
    isSubmitting,
    submitError,
    successMessage,
    handleSubmit,
    clearMessages,
  } = useFormSubmission({
    onSuccess: () => {
      setResetSuccess(true);
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate("/login", { 
          state: { message: "Password reset successfully! Please log in with your new password." }
        });
      }, 3000);
    },
    successMessage: "Your password has been reset successfully! Redirecting to login...",
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!token) return;

      await handleSubmit(async () => {
        await resetPassword({
          token,
          password: values.password,
          confirmPassword: values.confirmPassword,
        }).unwrap();
      });
    },
  });

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
  }, [token, navigate]);

  useEffect(() => {
    clearMessages();
  }, [clearMessages]);

  if (!token) {
    return null; // Will redirect in useEffect
  }

  if (isLoadingInfo) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2, ml: 2 }}>
            Validating reset token...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (infoError || !resetInfo?.isValid) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {resetInfo?.errorMessage || "This password reset link is invalid or has expired."}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please request a new password reset link or contact support if you continue to have issues.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={() => navigate("/forgot-password")}
            >
              Request New Link
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/login")}
            >
              Back to Login
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  if (resetSuccess) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Your password has been reset successfully. You will be redirected to the login page shortly.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Redirecting to login page...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <BaseFormPage
      title="Reset Password"
      subtitle={resetInfo ? `Welcome back, ${resetInfo.firstName} ${resetInfo.lastName}! Please enter your new password.` : "Please enter your new password."}
      onBack={() => navigate("/login")}
      formik={formik}
      isLoading={isLoadingInfo}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      submitButtonText="Reset Password"
      showKeyboardShortcuts={false}
      showUnsavedChangesWarning={false}
      maxWidth="sm"
    >
      <Box sx={{ position: "relative" }}>
        <GenericTextField
          name="password"
          label="New Password"
          type={showPassword ? "text" : "password"}
          required
          size="medium"
          placeholder="Enter your new password"
        />
        <Button
          size="small"
          onClick={() => setShowPassword(!showPassword)}
          sx={{ 
            position: "absolute", 
            right: 8, 
            top: "50%", 
            transform: "translateY(-50%)",
            minWidth: "auto", 
            px: 1 
          }}
        >
          {showPassword ? "Hide" : "Show"}
        </Button>
      </Box>

      <Box sx={{ position: "relative" }}>
        <GenericTextField
          name="confirmPassword"
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          required
          size="medium"
          placeholder="Confirm your new password"
        />
        <Button
          size="small"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          sx={{ 
            position: "absolute", 
            right: 8, 
            top: "50%", 
            transform: "translateY(-50%)",
            minWidth: "auto", 
            px: 1 
          }}
        >
          {showConfirmPassword ? "Hide" : "Show"}
        </Button>
      </Box>

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          Remember your password?{" "}
          <Button
            variant="text"
            onClick={() => navigate("/login")}
            sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
          >
            Sign in
          </Button>
        </Typography>
      </Box>
    </BaseFormPage>
  );
};

export default ResetPasswordPage;
